import { ActionIcon, Menu } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { skipToken, useQuery } from "@tanstack/react-query";
import { Link, generatePath, useResolvedPath } from "react-router";
import { apiWithoutCamelization } from "../../../api";
import { useRouteSiteId } from "../../../hooks/useRouteSiteId";
import { useSiteVariantId } from "../../../hooks/useSiteVariantId";
import urls from "../../../urls";
import type { Site } from "../../../utils/backend-types";
import { MenuItem } from "../MenuItem/MenuItem";
import { PageTitle } from "../PageTitle/PageTitle";
import { getSiteTitle } from "./utils/getSiteTitle";

function SiteBreadcrumbs() {
  const currentSiteId = useRouteSiteId();
  const { variantId } = useSiteVariantId(currentSiteId, !currentSiteId);
  const { data: sites } = useQuery({
    queryKey: ["sites-list", { variantId }],
    queryFn: variantId ? () => fetchSitesList(variantId) : skipToken,
    refetchInterval: false,
    refetchOnWindowFocus: false
  });

  if (!sites) {
    return null;
  }

  const currentSite = sites?.find((site) => site.id === currentSiteId);
  const pageTitle = currentSite
    ? getSiteTitle(currentSite)
    : "Unbekannte Liegenschaft";

  return (
    <>
      <PageTitle title={pageTitle} />
      {sites.length > 1 && (
        <MenuItem>
          <SiteSubMenu currentSiteId={currentSiteId} sites={sites} />
        </MenuItem>
      )}
    </>
  );
}

interface SiteSubMenuProps {
  sites: Array<Site>;
  currentSiteId?: number;
}

function SiteSubMenu({ sites, currentSiteId }: SiteSubMenuProps) {
  const resolved = useResolvedPath("../:siteId");

  const items = sites
    .filter((site) => site.id !== currentSiteId)
    .map((site) => {
      const siteUrl = generatePath(resolved.pathname, {
        siteId: site.id.toString()
      });

      return (
        <Menu.Item component={Link} key={site.id} to={siteUrl}>
          {getSiteTitle(site)}
        </Menu.Item>
      );
    });

  return (
    <Menu aria-label="Liegenschaftsauswahl">
      <Menu.Target>
        <ActionIcon c="neutral.8" mt={4} size="xs" variant="white">
          <IconChevronDown strokeWidth={1} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown
        mah="calc(100vh - 100px)"
        maw={800}
        style={{ overflowY: "auto" }}
      >
        {items}
      </Menu.Dropdown>
    </Menu>
  );
}

async function fetchSitesList(variantId: number) {
  const response = await apiWithoutCamelization.get<Array<Site>>(
    urls.apiWithoutCamelization.sitesList(variantId)
  );
  return response.data;
}

export { SiteBreadcrumbs };
